import { Injectable, Injector } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';

export interface ClientErrorReport {
  /** English sentence naming what failed, on what, and why. */
  message: string;
  /** Stable snake_case name; the event is stored as `frontend_<name>`. */
  name: string;
  stack?: string;
  /** Anything the page knew about the failure (file name, owner slug, browser error). Never a secret. */
  context?: Record<string, unknown>;
}

/**
 * Writes a frontend error to the backend event log (screen /admin/app-logs). Used by the global
 * AppErrorHandler, and by pages that handle an error the server never saw — a request the browser
 * refused to send has no server-side trace at all otherwise.
 *
 * Goes through HttpBackend, not HttpClient, so it skips the interceptors: JwtInterceptor logs the
 * user out on a 401, and a failing error report must never do that. Best effort, never throws.
 */
@Injectable({ providedIn: 'root' })
export class ClientErrorReportService {
  private rawHttp: HttpClient | null = null;

  constructor(private injector: Injector) {}

  report(report: ClientErrorReport): void {
    try {
      // Resolved lazily: the ErrorHandler uses this service and must not require HttpClient
      // (through AuthService) at construction time.
      const token = this.injector.get(AuthService).accessToken();
      if (!token || !report.message) {
        return;
      }
      if (!this.rawHttp) {
        this.rawHttp = new HttpClient(this.injector.get(HttpBackend));
      }
      this.rawHttp.post(
        environment.serverUrl + 'monitoring_app_log/report_client_error',
        {
          message: report.message.slice(0, 1000),
          name: report.name || 'error',
          stack: (report.stack || '').slice(0, 4000),
          url: typeof window !== 'undefined' ? window.location.pathname + window.location.search : '',
          context: report.context ?? null,
        },
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: token,
            'X-CSRF-Token': sessionStorage.getItem('csrf_token') || '',
          }),
        },
      ).subscribe({ error: () => undefined });
    } catch {
      // Reporting is best effort.
    }
  }
}
